import {
  type LeadGenerationRunReadable,
  LeadGenerationRunStatusEnum
} from '@crm/api'
import dayjs, { type Dayjs } from 'dayjs'

export const RUN_TIME_FORMAT = 'HH:mm'
export const RUN_TIME_API_FORMAT = 'HH:mm:ss'

export const RUNS_POLL_INTERVAL = 5000

export const runStatusColor: Record<LeadGenerationRunStatusEnum, string> = {
  started: 'blue',
  running: 'processing',
  finished: 'green',
  finished_with_errors: 'purple',
  failed: 'red',
  reaped: 'orange'
}

const ACTIVE_RUN_STATUSES = new Set<LeadGenerationRunStatusEnum>([
  LeadGenerationRunStatusEnum.STARTED,
  LeadGenerationRunStatusEnum.RUNNING
])

/**
 * The API serialises `run_time` as a bare `HH:mm:ss` clock value. Anchoring it
 * to a fixed date makes it an ISO string dayjs parses natively, so this does
 * not depend on the `customParseFormat` plugin being registered first.
 */
export const parseRunTime = (value?: string | null): Dayjs | undefined => {
  if (!value) return undefined

  const parsed = dayjs(`1970-01-01T${value}`)

  return parsed.isValid() ? parsed : undefined
}

export const formatRunTime = (value?: string | null) =>
  parseRunTime(value)?.format(RUN_TIME_FORMAT) ?? '-'

export const toApiRunTime = (value?: Dayjs | null) =>
  value ? value.format(RUN_TIME_API_FORMAT) : undefined

export const hasActiveRun = (runs?: LeadGenerationRunReadable[]) =>
  !!runs?.some(({ status }) => ACTIVE_RUN_STATUSES.has(status))

/**
 * `error_details` collects one message per candidate the run could not process.
 * The field already changed shape once (a plain string before it became a list),
 * so a bare string and a JSON-encoded array are both accepted, and anything that
 * does not parse is kept whole as a single entry rather than dropped.
 */
export const parseRunErrors = (
  errorDetails?: string | string[] | null
): string[] => {
  if (!errorDetails) return []

  if (Array.isArray(errorDetails)) {
    return errorDetails.filter(Boolean).map(String)
  }

  const trimmed = errorDetails.trim()

  if (!trimmed) return []
  if (!trimmed.startsWith('[')) return [trimmed]

  try {
    const parsed: unknown = JSON.parse(trimmed)

    return Array.isArray(parsed)
      ? parsed.filter(Boolean).map(String)
      : [trimmed]
  } catch {
    return [trimmed]
  }
}
